// Note this is client-side stripe API, so you cant call things like stripe.customers.retrieve etc
import {
  TextField,
  Box,
  LinearProgress,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Stack
} from "@mui/material";
import UnsubscribeIcon from "@mui/icons-material/Unsubscribe";
import SearchIcon from "@mui/icons-material/Search";
import BatchPredictionIcon from '@mui/icons-material/BatchPrediction';
import { POLLING_CONFIG } from '../config/appConfig';
import StopIcon from '@mui/icons-material/Stop';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';

import { useContext, useState, useRef } from "react";
import { CognitoUserContext } from "../contexts/cognitoUserContext.js";
import { API_REKOGNITION } from "../constants";

import { useSearch } from "../contexts/searchContext";
import { API } from "aws-amplify";
import { UpdateSlugPerson } from "./SlugPersonManagement.js";


export default function SlugAdmin() {

  const { filteredPeople } = useSearch();
  const { cognitoUser } = useContext(CognitoUserContext);

  const [importFilename, setImportFilename] = useState("HOP20260523.json");
  const [importProgress, setImportProgress] = useState({
    isImporting: false,
    isPaused: false,
    current: 0,
    total: 0,
    failures: 0,
    currentPerson: ''
  });
  const [rebuildProgress, setRebuildProgress] = useState({
    isRebuilding: false,
    message: ''
  });
  const stopImportRef = useRef(false);
  const continueImportRef = useRef(null);

  async function handleBulkHopUpdateClick(e) {
    for (const person of filteredPeople) {
      let minPerson = {};
      const propertyNames = Object.getOwnPropertyNames(person);
      for (const key of propertyNames) {
        const value = person[key];
        if (value !== null) {
          minPerson[key] = value;
        }
      }
      minPerson["dataset"] = "HOP"; // BIMPORT
      UpdateSlugPerson(cognitoUser, minPerson);
    }
  }

  function handleStopImport() {
    stopImportRef.current = true;
  }

  function handleContinueImport() {
    setImportProgress(prev => ({ ...prev, isPaused: false }));
    if (continueImportRef.current) {
      continueImportRef.current();
    }
  }

  async function handleBulkImportClick(e) {
    // Reset stop flag
    stopImportRef.current = false;

    // Dynamically import the selected file
    try {
      const personsToImport = await import(`../../data/${importFilename}`);
      bulkImport(personsToImport.default);
    } catch (error) {
      console.error(`Failed to load file: ${importFilename}`, error);
      alert(`Error loading file: ${importFilename}. Please check the filename.`);
    }
    return;
  }

  const bulkImport = async (personsToImport) => {
    const myAPI = "addslugpersonapi";
    const path = "/addperson";

    // Initialize progress
    setImportProgress({
      isImporting: true,
      isPaused: false,
      current: 0,
      total: personsToImport.length,
      failures: 0,
      currentPerson: ''
    });

    const addPeople = () => {
      (async function () {
        let count = 1;
        const failures = [];
        const startTime = new Date();

        for (const person of personsToImport) {
          // Check if stop was requested
          if (stopImportRef.current) {
            alert(`Import stopped by user.\nImported: ${count - 1}/${personsToImport.length}\nFailed: ${failures.length}`);
            break;
          }

          const personName = `${person.first_name} ${person.last_name}`.trim();

          // Update progress before processing
          // eslint-disable-next-line no-loop-func
          setImportProgress(prev => ({
            ...prev,
            current: count,
            currentPerson: personName
          }));

          // Retry logic for network errors
          let retryCount = 0;
          let success = false;

          while (!success && retryCount < 3) {
            try {
              let personArray = [];
              personArray.push(person);
              await API.put(myAPI, path, {
                queryStringParameters: {
                  person: JSON.stringify(personArray),
                },
              });

              // API.put returns the response body directly (with message: 'Success')
              // If we get here without throwing, the request succeeded
              success = true;
            } catch (error) {
              retryCount++;

              // If it's a network error and we have retries left, wait and retry
              if ((error.message.includes('Network Error') || error.message.includes('502')) && retryCount < 3) {
                console.warn(
                  `${count}/${personsToImport.length} ⚠ Retry ${retryCount}/3: ${person.person_id} ${personName} - ${error.message}`
                );
                // eslint-disable-next-line no-loop-func
                await new Promise(resolve => setTimeout(resolve, 1000 * retryCount)); // Exponential backoff
                continue;
              }

              // Final failure after retries
              const failureRecord = {
                person_id: person.person_id,
                name: personName,
                persontype: person.persontype,
                error: error.message || String(error),
                timestamp: new Date().toISOString()
              };
              failures.push(failureRecord);

              // Update failure count immediately
              setImportProgress(prev => ({
                ...prev,
                failures: failures.length
              }));

              console.error(
                `${count}/${personsToImport.length} ✗ FAILED: ${person.person_id} ${personName} - ${error.message} (after ${retryCount} retries)`
              );
              break;
            }
          }

          // Pause after first record
          if (count === 1) {
            setImportProgress(prev => ({ ...prev, isPaused: true }));

            // Wait for user to continue
            await new Promise((resolve) => {
              continueImportRef.current = resolve;
            });
          } else {
            // Add 200ms delay between records (after first record)
            await new Promise(resolve => setTimeout(resolve, POLLING_CONFIG.API_RETRY_DELAY));
          }

          count = count + 1;
        }

        // Write failures to downloadable JSON file
        const endTime = new Date();
        const duration = ((endTime - startTime) / 1000 / 60).toFixed(2);

        if (failures.length > 0) {
          const failureReport = {
            import_file: importFilename,
            total_records: personsToImport.length,
            successful: personsToImport.length - failures.length,
            failed: failures.length,
            duration_minutes: duration,
            timestamp: endTime.toISOString(),
            failures: failures
          };

          // Create downloadable blob
          const blob = new Blob([JSON.stringify(failureReport, null, 2)], {
            type: 'application/json'
          });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `import_failures_${importFilename.replace('.json', '')}_${Date.now()}.json`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          console.error(`Import completed with ${failures.length} failures. Report downloaded.`);
          alert(`Import completed!\nSuccessful: ${personsToImport.length - failures.length}\nFailed: ${failures.length}\n\nFailure report downloaded.`);
        } else {

          // Ask if user wants to rebuild faces collection
          const shouldRebuild = window.confirm(
            `Import completed successfully!\nAll ${personsToImport.length} records imported in ${duration} minutes.\n\nDo you want to rebuild the Rekognition faces collection now?`
          );

          if (shouldRebuild) {
            handleRebuildFacesClick();
          } else {
            alert(`Import completed successfully!\nAll ${personsToImport.length} records imported in ${duration} minutes.`);
          }
        }

        // Reset progress
        setImportProgress({
          isImporting: false,
          current: 0,
          total: 0,
          failures: 0,
          currentPerson: ''
        });
      })();
    };
    addPeople();
  };

  async function handleSearchCreateFieldClick(e) {
    let successCount = 0;
    let failCount = 0;

    for (const person of filteredPeople) {
      // Create search string
      const first_name = person.first_name || "";
      const last_name = person.last_name || "";
      const constituency = person.constituency || "";
      const searchstring = `${first_name} ${last_name} ${constituency}`.toLowerCase();

      // Build update payload with ALL required fields (Lambda expects them)
      const updatePayload = {
        person_id: person.person_id,
        searchstring: searchstring,
        dataset: person.dataset || "HOP",
        party: person.party || "",
        persontype: person.persontype || "",
        facialhair: person.facialhair || "",
        gender: person.gender || "",
        agegroup: person.agegroup || "",
        glasses: person.glasses || "",
        skintone: person.skintone || "",
        hairstyle: person.hairstyle || "",
        haircolor: person.haircolor || ""
      };

      try {
        await singleUpdatePerson(updatePayload);
        successCount++;

        // Add small delay to avoid throttling
        await new Promise(resolve => setTimeout(resolve, POLLING_CONFIG.BATCH_OPERATION_DELAY));
      } catch (error) {
        console.error(`Failed to update ${person.person_id}:`, error.message);
        failCount++;
      }
    }

    alert(`Search field generation complete!\nSuccessful: ${successCount}\nFailed: ${failCount}`);
  }

  async function singleUpdatePerson(person) {
    const myAPI = "addslugpersonapi";
    const path = "/updateperson";

    const res = await API.put(myAPI, path, {
      queryStringParameters: {
        person: JSON.stringify(person),
      },
    });

    return res;
  }

  async function handleRebuildFacesClick(e) {
    if (!window.confirm('This will rebuild the entire Rekognition faces collection. This will run in the background and may take 10-15 minutes. Continue?')) {
      return;
    }

    setRebuildProgress({
      isRebuilding: true,
      message: 'Starting rebuild...'
    });

    try {
      const myAPI = API_REKOGNITION;
      const path = "/rebuild";

      const response = await API.get(myAPI, path);


      // Check if it's an async response (202 status or has 'status' field)
      if (response.status === 'processing' || response.message === 'Rebuild started') {
        alert(`${response.message}\n\n${response.note}\n\nYou can continue working while the rebuild runs in the background. Check CloudWatch logs for progress.`);

        setRebuildProgress({
          isRebuilding: false,
          message: ''
        });
      } else {
        // Synchronous completion (shouldn't happen with new code, but handle it)
        const summary = `Rebuild complete!\n\nDeleted: ${response.faces_deleted} faces\nProcessed: ${response.persons_processed} persons\nIndexed: ${response.faces_indexed} faces\nFailed: ${response.failures_count}\nDuration: ${response.duration_minutes} minutes`;

        alert(summary);

        setRebuildProgress({
          isRebuilding: false,
          message: ''
        });
      }

    } catch (error) {
      console.error('Error rebuilding faces:', error);
      alert(`Error starting rebuild:\n${error.message}`);

      setRebuildProgress({
        isRebuilding: false,
        message: ''
      });
    }
  }

  return (
    <Stack spacing={3}>
      {/* Bulk Import Section */}
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <UnsubscribeIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Bulk Import People</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Import people records from JSON files located in src/forimport/. The system will process each record with retry logic and pause after the first import for verification.
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Import Filename"
            value={importFilename}
            onChange={(e) => setImportFilename(e.target.value)}
            helperText="Enter filename (e.g., HOP20260523.json)"
            disabled={importProgress.isImporting}
          />
        </CardContent>
        <CardActions>
          <Button
            disabled={true}
            onClick={handleBulkImportClick}
            variant="contained"
            startIcon={<UnsubscribeIcon />}
          >
            Start Import
          </Button>
        </CardActions>
      </Card>

      {/* Import Progress */}
      {importProgress.isImporting && (
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Import Progress {importProgress.isPaused && ' - PAUSED'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {importProgress.current} / {importProgress.total}
                {importProgress.failures > 0 && ` (${importProgress.failures} failed)`}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(importProgress.current / importProgress.total) * 100}
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              Current: {importProgress.currentPerson}
            </Typography>
            {importProgress.isPaused && (
              <Typography variant="body2" color="success.main" sx={{ mt: 2, fontWeight: 'bold' }}>
                ✓ First record imported successfully. Review and click Continue to import remaining records.
              </Typography>
            )}
          </CardContent>
          <CardActions sx={{ justifyContent: 'flex-end' }}>
            {importProgress.isPaused ? (
              <>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<StopIcon />}
                  onClick={handleStopImport}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleContinueImport}
                >
                  Continue
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                color="error"
                startIcon={<StopIcon />}
                onClick={handleStopImport}
              >
                Stop Import
              </Button>
            )}
          </CardActions>
        </Card>
      )}

      {/* Data Management */}
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <BatchPredictionIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Update HOP Dataset</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Add or update House of Parliament (HOP) data for all currently filtered people. This bulk operation will update searchable attributes.
          </Typography>
        </CardContent>
        <CardActions>
          <Button
            onClick={handleBulkHopUpdateClick}
            variant="outlined"
            startIcon={<BatchPredictionIcon />}
          >
            Add HOP Data
          </Button>
        </CardActions>
      </Card>

      {/* Search Field Generation */}
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SearchIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Generate Search Fields</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Create searchable text fields for all filtered people by combining their first name, last name, and constituency. This improves search performance.
          </Typography>
        </CardContent>
        <CardActions>
          <Button
            onClick={handleSearchCreateFieldClick}
            variant="outlined"
            startIcon={<SearchIcon />}
          >
            Create Search Fields
          </Button>
        </CardActions>
      </Card>

      {/* Rekognition Faces */}
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FaceRetouchingNaturalIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Rebuild Face Recognition</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Rebuild the AWS Rekognition faces collection from all people records with images. This process runs asynchronously in the background and may take 10-15 minutes for ~1,400 faces.
          </Typography>
          {rebuildProgress.isRebuilding && (
            <Box>
              <LinearProgress sx={{ mb: 1 }} />
              <Typography variant="caption" color="primary">
                {rebuildProgress.message || 'Rebuilding faces collection...'}
              </Typography>
            </Box>
          )}
        </CardContent>
        <CardActions>
          <Button
            disabled={rebuildProgress.isRebuilding}
            onClick={handleRebuildFacesClick}
            variant="outlined"
            startIcon={<FaceRetouchingNaturalIcon />}
          >
            {rebuildProgress.isRebuilding ? 'Rebuilding...' : 'Rebuild Collection'}
          </Button>
        </CardActions>
      </Card>
    </Stack>
  );
}
